import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import type { DiagnosticEvents } from "./types";
import { formatTimestamp } from "./types";

interface DiagnosticEventsDisplayProps {
  events?: DiagnosticEvents;
  title?: string;
}

export function DiagnosticEventsDisplay({
  events,
  title,
}: DiagnosticEventsDisplayProps) {
  if (!events?.events?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No diagnostic events
      </Typography>
    );
  }

  const sortedEvents = [...events.events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const getIcon = (level: string) => {
    switch (level) {
      case "error":
        return <ErrorIcon color="error" fontSize="small" />;
      case "warn":
        return <WarningIcon color="warning" fontSize="small" />;
      default:
        return <InfoIcon color="info" fontSize="small" />;
    }
  };

  const getColor = (level: string) => {
    switch (level) {
      case "error":
        return "error.main";
      case "warn":
        return "warning.main";
      default:
        return "text.secondary";
    }
  };

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}
      <List dense sx={{ py: 0 }}>
        {sortedEvents.map((event, idx) => (
          <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>{getIcon(event.level)}</ListItemIcon>
            <ListItemText
              primary={event.message}
              secondary={formatTimestamp(event.timestamp)}
              primaryTypographyProps={{
                variant: "body2",
                sx: { color: getColor(event.level) },
              }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
